import {PropsWithChildren} from 'react';
import {useTranslation} from 'react-i18next';
import styled, {css} from 'styled-components';

type ButtonProps = PropsWithChildren<{
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset' | undefined;
  onClick?: React.MouseEventHandler<HTMLElement>;
  size?: 'small';
  outline?: boolean;
}>;

const Button = ({
  loading,
  children,
  type,
  size,
  onClick,
  outline,
}: ButtonProps) => {
  const {t} = useTranslation();

  return (
    <ButtonStyled
      size={size}
      className={loading ? 'loading' : ''}
      type={type}
      onClick={onClick}
      outline={outline}
    >
      {children}
      {loading && <LoadingText>{t('general.loading')}</LoadingText>}
    </ButtonStyled>
  );
};

export default Button;

const ButtonStyled = styled.button<{
  size?: 'small';
  outline?: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 17px 74px;
  border-radius: 10px;
  border: none;
  background-color: ${(props) => props.theme.colors.orange500};
  font-size: 1rem;
  font-weight: 700;
  font-family: 'Mulish', sans-serif;
  color: ${(props) => props.theme.colors.white};
  transition: background-color ease 0.2s;
  cursor: pointer;
  text-decoration: none;

  svg {
    margin-right: 8px;
  }

  ${(p) =>
    p.size === 'small' &&
    css`
      padding: 12px 24px;
      font-size: 14px;
      font-family: 'Mulish', sans-serif;
      white-space: nowrap;
    `}

  &.loading {
    opacity: 0.8;
    cursor: default;
    pointer-events: none;
    color: transparent;
  }

  &::after {
    content: '';
    position: absolute;
    width: 80%;
    height: 50%;
    top: 70%;
    left: 50%;
    transform: translateX(-50%);
    background-color: ${(props) => props.theme.colors.orange500};
    z-index: -1;
    filter: blur(15px);
  }

  &:hover {
    background-color: #eb5530;

    &::after {
      height: 40%;
    }
  }

  a {
    text-decoration: none;
  }

  ${(p) =>
    p.outline &&
    css`
      color: #360ac8;
      background-color: #f5f5f5;
      padding: 17px 40px;

      &::after {
        content: initial;
      }

      &:hover {
        background-color: #f5f5f5;
        box-shadow: 0 0 0 2px #360ac8;
      }
    `}
`;

const LoadingText = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  color: white;
`;
